import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import type { AccountFlags, PersonalAccess } from '../administration/api';
import type { Ebenen } from '../components/menuEntries';
import { istPflichtFehler } from '../components/account/zweiFaktor';
import { istRichtlinienFehler } from '../administration/recht/richtlinieTexte';
import { farbschemaAbgleich, readThemeChoice, saveThemeChoice } from '../utils/theme';
import { alterChatModus, chatModusUebernahme } from '../pages/einstellungen/praeferenzen';

// Laufende Sicht und Startrechte des echten Kontos (SPEC VE-20, VE-29); einzige Quelle für Schalter und Band.
export interface SichtAktiv {
  art: 'PREVIEW' | 'IMPERSONATION';
  linse: 'TENANT_ADMIN' | 'TENANT_USER';
  firmaId: string;
  firmaName: string;
  zusatzrolle: { id: string; name: string } | null;
  bis: string;
  startebene: 'VERWALTUNG' | 'ADMIN_EINSTELLUNGEN' | 'MEINE_EINSTELLUNGEN' | 'PRODUKT';
  rueckweg: string;
}
export interface SichtStarten {
  vorschau: boolean;
  vertretung: boolean;
  nurUser: boolean;
  heimatfirma: { id: string; name: string } | null;
}
export interface Sicht { aktiv: SichtAktiv | null; starten: SichtStarten }

/**
 * Die vom Server gemeldeten Fähigkeiten dieses Kontos. `developer` entscheidet
 * über den Vergleich und den Modellschalter; die Oberfläche leitet das nie
 * selbst aus Plan oder Rolle ab. Fehlt das Feld, gilt das Konto als gewöhnlich.
 */
export interface UserCapabilities {
  developer?: boolean;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  platformAccount?: boolean;
  canManagePlatform?: boolean;
  subscriptionPlan?: string;
  capabilities?: UserCapabilities;
  organizationId?: string;
  organizationRole?: 'MEMBER' | 'ADMIN';
  organizationAccessActive?: boolean;
  // Admin-Wünsche W3 (PLAN 2.3): „Konto seit“ und „Vorherige Anmeldung“ im Profil (ISO-Zeitpunkte aus GET /api/auth/profile);
  // previousLoginAt null = noch keine frühere Anmeldung gespeichert. Die Rechte des Kontos (canChangePlan, rolle,
  // twoFactor) stehen in account (AccountFlags, administration/api.ts).
  createdAt?: string;
  previousLoginAt?: string | null;
  access?: PersonalAccess;
  // Sammel-Nachbesserung N-C und Paket 08 (M-01.6): die Fähigkeiten des eigenen Kontos, vom Server entschieden
  // (canOpenBilling nach dem Leseweg der Abrechnung); fehlt das Feld, gilt „nicht zeigen“.
  account?: AccountFlags;
  // Drei Ebenen (VE-02): vom Server entschieden; ein fehlendes Feld heißt „nicht zeigen“.
  ebenen?: Ebenen;
  sicht?: Sicht;
}

/** Ein Entwicklerkonto, so wie der Server es gemeldet hat. */
export function hasDeveloperCapability(user: User | null | undefined): boolean {
  return user?.capabilities?.developer === true;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Entwicklerkonto: Vergleich, Modellschalter und Bahn C stehen offen. */
  isDeveloper: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<'angemeldet' | 'zweiFaktor'>;
  loginZweiFaktor: (eingabe: { code: string } | { wiederherstellungscode: string }) => Promise<void>;
  logout: () => Promise<void>;
  // Profil-Nachbau F-02 (F-PR-1): „Alle Sitzungen beenden“ – jede Anmeldung des Kontos endet sofort, auch diese.
  logoutAll: () => Promise<void>;
  checkAuth: () => Promise<void>;
  // Paket AA: der Server verlangt die Einrichtung der Zwei-Faktor-Anmeldung (403 TWO_FACTOR_SETUP_REQUIRED).
  zweiFaktorPflicht: boolean;
  // Profil-Nachbau F-17 (PF-782, BAU-RECHT.md 3.2): die Firma hat eine neue Fassung der Nutzungsrichtlinie, die das Konto noch
  // bestätigen muss (403 USAGE_POLICY_REQUIRED); App.tsx zeigt dann nur die Zustimmungsseite (PF-787), wie bei zweiFaktorPflicht.
  richtliniePflicht: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [zweiFaktorPflicht, setZweiFaktorPflicht] = useState(false);
  const [richtliniePflicht, setRichtliniePflicht] = useState(false);

  // Check if user is authenticated by calling /api/auth/me
  const checkAuth = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PROFILE);
      setUser(response.data.user);
      setZweiFaktorPflicht(false);
      setRichtliniePflicht(false);
      // Profil-Nachbau F-09 (BAU-PRAEFERENZEN.md 1.2): das Farbschema des Kontos gilt auf jedem Gerät; ohne Wahl am Konto wird die
      // dieses Browsers einmal übernommen. In einer Sicht gilt nur der Browser (dort schreibt der Server nichts).
      const profil = response.data.user as User | null;
      if (profil && !profil.sicht?.aktiv) {
        const abgleich = farbschemaAbgleich(profil.account?.colorScheme, readThemeChoice());
        if (abgleich.anwenden) saveThemeChoice(abgleich.anwenden);
        if (abgleich.uebernehmen) void apiClient.put('/api/auth/preferences', { colorScheme: abgleich.uebernehmen }).catch(() => undefined);
        // Profil-Nachbau F-09 (BAU-PRAEFERENZEN.md 1.4): hat das Konto für „Mit Enter senden“ noch keine Wahl (null), wird die frühere
        // Wahl am Eingabefeld einmal übernommen; danach gilt nur noch das Konto. In einer Sicht nie.
        const chatModus = chatModusUebernahme(profil.account?.sendWithEnter, alterChatModus());
        if (chatModus !== undefined) void apiClient.put('/api/auth/preferences', { sendWithEnter: chatModus }).catch(() => undefined);
      }
    } catch (error) {
      setUser(null);
      setZweiFaktorPflicht(istPflichtFehler(error));
      setRichtliniePflicht(istRichtlinienFehler(error));
    } finally {
      setLoading(false);
    }
  }, []);

  // Login - server sets httpOnly cookie
  const login = useCallback(async (email: string, password: string): Promise<'angemeldet' | 'zweiFaktor'> => {
    const response = await apiClient.post(API_ENDPOINTS.LOGIN, { email, password });
    // Paket AA: mit Zwei-Faktor gibt es erst nach dem Code eine Sitzung.
    if (response.data?.twoFactorRequired) return 'zweiFaktor';
    // Publish the authoritative profile including scoped permissions once.
    // An interim login DTO can redirect delegated administrators before /me resolves.
    await checkAuth();
    return 'angemeldet';
  }, [checkAuth]);

  // Paket AA: der zweite Schritt mit dem Code aus der App oder einem Wiederherstellungscode; erst dann gibt es die Sitzung.
  const loginZweiFaktor = useCallback(async (eingabe: { code: string } | { wiederherstellungscode: string }) => {
    await apiClient.post('/api/auth/login/two-factor', eingabe);
    await checkAuth();
  }, [checkAuth]);

  // Logout - server clears httpOnly cookie. Profil-Nachbau F-02 (F-PR-1): der Server beendet nur die Sitzung dieses Geräts.
  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      // Ignore errors, clear state anyway
    }
    setUser(null);
    setZweiFaktorPflicht(false);
    setRichtliniePflicht(false);
  }, []);

  // Profil-Nachbau F-02 (F-PR-1): beendet jede Anmeldung des Kontos (POST /api/auth/sessions/end-all). Anders als logout
  // gibt es den Fehler weiter: Scheitert der Aufruf, laufen die anderen Geräte weiter, und die Seite muss das sagen.
  const logoutAll = useCallback(async () => {
    await apiClient.post('/api/auth/sessions/end-all');
    setUser(null);
    setZweiFaktorPflicht(false);
    setRichtliniePflicht(false);
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for auth:logout events (from apiClient 401 handler)
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      // Paket AA: ohne Sitzung gibt es auch keine Pflichtseite mehr, sondern die Anmeldung.
      setZweiFaktorPflicht(false);
      setRichtliniePflicht(false);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  useEffect(() => {
    const refreshAccess = () => { void checkAuth(); };
    window.addEventListener('auth:access-changed', refreshAccess);
    return () => window.removeEventListener('auth:access-changed', refreshAccess);
  }, [checkAuth]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    // Paket 15, Plan-Schritt 8 (M-01.6): only server fields decide; a missing field means "not allowed".
    isAdmin: user?.canManagePlatform === true,
    isDeveloper: hasDeveloperCapability(user),
    loading,
    login,
    loginZweiFaktor,
    logout,
    logoutAll,
    checkAuth,
    zweiFaktorPflicht,
    richtliniePflicht,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
